import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { getAdminSettings, getPatientRecord, resolveMediaUrl, getPublicApiOrigin, deleteClinicalEncounter } from '../lib/api';
import {
  resolveClinicalFormTemplate,
  clinicalFormVisitsOnly,
  CLINICAL_FORM_REHABILITA,
  CLINICAL_FORM_PELUQUERIA,
  CLINICAL_FORM_DENTAL,
  CLINICAL_FORM_GENERAL,
} from '../lib/clinicalFormTemplates';
import { canPrintEvolutionNote, createAndOpenEvolutionNote } from '../lib/clinicalEncounterNav';
import IntakeWizard from '../components/clinical/IntakeWizard';
import RehabilitaIntakeForm from '../components/clinical/RehabilitaIntakeForm';
import SalonIntakeForm from '../components/clinical/SalonIntakeForm';
import DentalIntakeForm from '../components/clinical/DentalIntakeForm';
import GeneralIntakeForm from '../components/clinical/GeneralIntakeForm';
import ClinicalAlertsBanner from '../components/clinical/ClinicalAlertsBanner';
import { toast } from 'sonner';
import { ArrowLeft, Calendar, ClipboardList, FileText, Plus, Trash2 } from 'lucide-react';
import { formatAppointmentDateOnlyInClinic } from '../lib/clinicTime';
import { useClinicBranding } from '../context/ClinicBrandingContext';
import { printEvolutionNote } from '../lib/printEvolutionNote';

function safeEncounterDate(enc) {
  const dateSrc = enc?.encounter_date || enc?.created_at;
  if (!dateSrc) return '';
  const raw = String(dateSrc);
  const ymd = raw.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return '';
  try {
    return formatAppointmentDateOnlyInClinic(ymd, 'd MMM yyyy');
  } catch {
    return ymd;
  }
}

function isPdfAttachment(att) {
  if (!att) return false;
  if (att.kind === 'file') return true;
  const ct = String(att.content_type || '').toLowerCase();
  const url = String(att.url || '').toLowerCase();
  const name = String(att.original_name || '').toLowerCase();
  return ct.includes('pdf') || url.endsWith('.pdf') || name.endsWith('.pdf');
}

function photoAttachments(enc) {
  return (enc?.attachments || []).filter((att) => att?.url && !isPdfAttachment(att));
}

export default function AdminPatientRecordDetail() {
  const { recordId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const appointmentId = searchParams.get('appointment');
  const navigate = useNavigate();
  const { info: clinicInfo } = useClinicBranding();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [printingId, setPrintingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [clinicalFormTemplate, setClinicalFormTemplate] = useState('rehabilita');

  const setTab = (next) => {
    const sp = new URLSearchParams(searchParams);
    if (next === 'encuentros') {
      sp.set('tab', 'encuentros');
    } else {
      sp.delete('tab');
    }
    setSearchParams(sp, { replace: true });
  };

  const reload = useCallback(async () => {
    if (!recordId) return;
    setLoading(true);
    try {
      const d = await getPatientRecord(recordId);
      setData(d);
    } catch {
      toast.error('No se pudo cargar el expediente');
    } finally {
      setLoading(false);
    }
  }, [recordId]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const settings = await getAdminSettings();
        if (alive) setClinicalFormTemplate(settings.clinical_form_template || 'rehabilita');
      } catch {
        /* mantener valor por defecto */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const record = data?.record;
  const encounters = Array.isArray(data?.encounters) ? data.encounters : [];
  const patient = record?.patient;
  const activeFormTemplate = resolveClinicalFormTemplate(
    record?.intake_adult,
    clinicalFormTemplate,
    record?.status
  );
  const visitsOnly = clinicalFormVisitsOnly(activeFormTemplate);
  const tab =
    visitsOnly || searchParams.get('tab') === 'encuentros' ? 'encuentros' : 'historia';

  useEffect(() => {
    if (!visitsOnly) return;
    if (searchParams.get('tab') === 'encuentros') return;
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', 'encuentros');
    setSearchParams(sp, { replace: true });
  }, [visitsOnly, searchParams, setSearchParams]);

  const startEncounterForAppointment = async () => {
    if (!appointmentId) return;
    try {
      const ok = await createAndOpenEvolutionNote(recordId, navigate, { appointmentId });
      if (!ok) toast.error('No se pudo crear la nota de evolución');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear la nota de evolución');
    }
  };

  const startNewEvolutionNote = async () => {
    try {
      const ok = await createAndOpenEvolutionNote(recordId, navigate);
      if (!ok) toast.error('No se pudo crear la nota de evolución');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo crear la nota de evolución');
    }
  };

  const handlePrintEncounter = async (enc) => {
    if (!enc?.id || !canPrintEvolutionNote(enc)) {
      toast.error('Esta nota no tiene contenido para imprimir');
      return;
    }
    setPrintingId(enc.id);
    try {
      const result = await printEvolutionNote(enc.id, { clinicInfo });
      if (!result.ok) {
        if (result.reason === 'empty_content') {
          toast.error(result.message || 'La nota no tiene contenido guardado');
        } else {
          toast.error('Permita ventanas emergentes para abrir el PDF.');
        }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo preparar la impresión');
    } finally {
      setPrintingId(null);
    }
  };

  const handleDeleteEncounter = async (enc) => {
    if (!enc?.id) return;
    const label = visitsOnly ? 'esta cita / nota' : 'esta nota de evolución';
    const ok = window.confirm(
      `¿Eliminar ${label}${enc.folio ? ` (${enc.folio})` : ''}? Se borrarán también sus fotos. Esta acción no se puede deshacer.`,
    );
    if (!ok) return;
    setDeletingId(enc.id);
    try {
      await deleteClinicalEncounter(enc.id);
      toast.success(visitsOnly ? 'Cita eliminada' : 'Nota eliminada');
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar la nota');
    } finally {
      setDeletingId(null);
    }
  };

  const encounterForAppt = encounters.find((e) => e.appointment_id === appointmentId);

  if (loading && !record) {
    return <p className="text-muted-foreground py-12 text-center">Cargando expediente…</p>;
  }

  if (!record) {
    return <p className="text-muted-foreground py-12 text-center">Expediente no encontrado</p>;
  }

  return (
    <div className="space-y-6 min-h-[50vh]">
      <div className="flex flex-wrap items-center gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/admin/expedientes">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Expedientes
          </Link>
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="font-heading text-2xl font-bold truncate">
            {patient?.full_name || 'Paciente'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {[patient?.phone, patient?.email].filter(Boolean).join(' · ') || 'Sin contacto'}
          </p>
        </div>
        {visitsOnly ? (
          <Badge variant="secondary">Historial de citas</Badge>
        ) : (
          <Badge variant={record.status === 'complete' ? 'default' : 'secondary'}>
            {record.status === 'complete' ? 'Historia completa' : 'Borrador'}
          </Badge>
        )}
      </div>

      {!visitsOnly ? <ClinicalAlertsBanner intake={record?.intake_adult} /> : null}

      {appointmentId ? (
        <Card className="border-primary/30 bg-primary/5">
          <CardContent className="py-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm">
              Vinculado a una cita.{' '}
              {encounterForAppt
                ? 'Ya existe una nota de evolución para esta visita.'
                : 'Inicie la nota de evolución de esta visita.'}
            </p>
            {encounterForAppt ? (
              <Button
                size="sm"
                className="rounded-full"
                onClick={() =>
                  navigate(`/admin/expedientes/${recordId}/encuentro/${encounterForAppt.id}`)
                }
              >
                Abrir nota de la cita
              </Button>
            ) : (
              <Button size="sm" className="rounded-full gap-1" onClick={startEncounterForAppointment}>
                <Plus className="w-4 h-4" />
                Iniciar nota de evolución
              </Button>
            )}
          </CardContent>
        </Card>
      ) : null}

      {!visitsOnly ? (
        <div className="flex flex-wrap gap-2 border-b border-border pb-2">
          <Button
            type="button"
            variant={tab === 'historia' ? 'default' : 'ghost'}
            size="sm"
            className="rounded-full gap-1"
            onClick={() => setTab('historia')}
          >
            <ClipboardList className="w-4 h-4" />
            Historia clínica
          </Button>
          <Button
            type="button"
            variant={tab === 'encuentros' ? 'default' : 'ghost'}
            size="sm"
            className="rounded-full gap-1"
            onClick={() => setTab('encuentros')}
          >
            <Calendar className="w-4 h-4" />
            Notas de evolución ({encounters.length})
          </Button>
        </div>
      ) : null}

      {tab === 'historia' && !visitsOnly ? (
        activeFormTemplate === CLINICAL_FORM_REHABILITA ? (
          <RehabilitaIntakeForm
            recordId={recordId}
            patientId={record.patient_id}
            patient={patient}
            initialIntake={record.intake_adult}
            recordStatus={record.status}
            onSaved={() => reload()}
            onCompleted={() => setTab('encuentros')}
          />
        ) : activeFormTemplate === CLINICAL_FORM_PELUQUERIA ? (
          <SalonIntakeForm
            recordId={recordId}
            patientId={record.patient_id}
            patient={patient}
            initialIntake={record.intake_adult}
            recordStatus={record.status}
            onSaved={() => reload()}
            onCompleted={() => setTab('encuentros')}
          />
        ) : activeFormTemplate === CLINICAL_FORM_DENTAL ? (
          <DentalIntakeForm
            recordId={recordId}
            patientId={record.patient_id}
            patient={patient}
            initialIntake={record.intake_adult}
            recordStatus={record.status}
            onSaved={() => reload()}
            onCompleted={() => setTab('encuentros')}
          />
        ) : activeFormTemplate === CLINICAL_FORM_GENERAL ? (
          <GeneralIntakeForm
            recordId={recordId}
            patientId={record.patient_id}
            patient={patient}
            initialIntake={record.intake_adult}
            recordStatus={record.status}
            onSaved={() => reload()}
            onCompleted={() => setTab('encuentros')}
          />
        ) : (
          <IntakeWizard
            recordId={recordId}
            patientId={record.patient_id}
            patient={patient}
            initialIntake={record.intake_adult}
            initialConsent={record.consent}
            recordStatus={record.status}
            onSaved={() => reload()}
            onCompleted={() => setTab('encuentros')}
          />
        )
      ) : (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-3">
            <div>
              <CardTitle>{visitsOnly ? 'Historial de citas' : 'Notas de evolución'}</CardTitle>
              {visitsOnly ? (
                <p className="text-sm text-muted-foreground mt-1 font-normal">
                  Cada cita queda como nota de evolución con fecha y fotos.
                </p>
              ) : null}
            </div>
            <Button size="sm" className="rounded-full gap-1 shrink-0" onClick={startNewEvolutionNote}>
              <Plus className="w-4 h-4" />
              {visitsOnly ? 'Nueva cita / nota' : 'Nueva nota de evolución'}
            </Button>
          </CardHeader>
          <CardContent className="space-y-3">
            {encounters.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                {visitsOnly
                  ? 'Aún no hay citas registradas. Crea la primera nota de evolución.'
                  : 'No hay notas de evolución registradas.'}
              </p>
            ) : (
              encounters.map((enc) => {
                const photos = photoAttachments(enc);
                return (
                  <div
                    key={enc.id || enc.created_at}
                    className="rounded-lg border p-3 space-y-3"
                  >
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <p className="font-medium text-sm">
                          {visitsOnly
                            ? 'Cita / nota de evolución'
                            : enc.encounter_type === 'intake'
                              ? 'Historia inicial'
                              : 'Nota de evolución'}
                          {enc.folio ? ` · ${enc.folio}` : ''}
                          {enc.appointment_id ? ' · cita vinculada' : ''}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {safeEncounterDate(enc)}
                          {' · '}
                          {enc.status === 'complete' ? 'Completa' : 'Borrador'}
                        </p>
                        {enc.chief_complaint ? (
                          <p className="text-sm mt-1 line-clamp-2">{enc.chief_complaint}</p>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {!visitsOnly && canPrintEvolutionNote(enc) ? (
                          <Button
                            size="sm"
                            variant="secondary"
                            disabled={printingId === enc.id}
                            onClick={() => handlePrintEncounter(enc)}
                          >
                            <FileText className="w-4 h-4 mr-1" />
                            {enc.status === 'complete' ? 'Reimprimir PDF' : 'Ver / Imprimir PDF'}
                          </Button>
                        ) : null}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/admin/expedientes/${recordId}/encuentro/${enc.id}`)}
                        >
                          Abrir
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={deletingId === enc.id}
                          onClick={() => handleDeleteEncounter(enc)}
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          {deletingId === enc.id ? 'Eliminando…' : 'Eliminar'}
                        </Button>
                      </div>
                    </div>
                    {photos.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {photos.slice(0, 8).map((att) => (
                          <a
                            key={att.id || att.url}
                            href={resolveMediaUrl(att.url)}
                            target="_blank"
                            rel="noreferrer"
                            className="block w-16 h-16 rounded-md border overflow-hidden bg-muted/40 shrink-0"
                            title={att.caption || att.original_name || 'Foto'}
                          >
                            <img
                              src={`${getPublicApiOrigin()}${att.url}`}
                              alt={att.caption || 'Foto de la cita'}
                              className="w-full h-full object-cover"
                            />
                          </a>
                        ))}
                        {photos.length > 8 ? (
                          <span className="text-xs text-muted-foreground self-center">
                            +{photos.length - 8} más
                          </span>
                        ) : null}
                      </div>
                    ) : visitsOnly ? (
                      <p className="text-xs text-muted-foreground">Sin fotos en esta cita</p>
                    ) : null}
                  </div>
                );
              })
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
